import { useMemo } from 'react'

export const ORG_FILTER_ALL = 'all'

export interface OrgSelectOption {
  value: number | string
  label: string
  siteId?: number | null
  departmentId?: number | null
}

interface UseOrgCascadeFiltersParams<TDept extends OrgSelectOption, TUnit extends OrgSelectOption> {
  siteId: string
  setSiteId: (val: string) => void
  departmentId: string
  setDepartmentId: (val: string) => void
  departmentOptions: TDept[]
  setUnitId?: (val: string) => void
  unitOptions?: TUnit[]
  // Value representing "nothing selected". Filter pages use the ORG_FILTER_ALL
  // sentinel (the default); required forms pass '' so empty means unselected.
  emptyValue?: string
  // When true (required forms), children stay empty until their parent is chosen
  // instead of falling back to the full list.
  requireParent?: boolean
}

interface UseOrgCascadeFiltersResult<TDept extends OrgSelectOption, TUnit extends OrgSelectOption> {
  filteredDepartmentOptions: TDept[]
  filteredUnitOptions: TUnit[]
  handleSiteChange: (val: string) => void
  handleDepartmentChange: (val: string) => void
}

export function useOrgCascadeFilters<TDept extends OrgSelectOption, TUnit extends OrgSelectOption>({
  siteId,
  setSiteId,
  departmentId,
  setDepartmentId,
  departmentOptions,
  setUnitId,
  unitOptions = [] as unknown as TUnit[],
  emptyValue = ORG_FILTER_ALL,
  requireParent = false,
}: UseOrgCascadeFiltersParams<TDept, TUnit>): UseOrgCascadeFiltersResult<TDept, TUnit> {
  const filteredDepartmentOptions = useMemo(() => {
    if (siteId === emptyValue) return requireParent ? [] : departmentOptions
    return departmentOptions.filter((dept) => dept.siteId == null || String(dept.siteId) === siteId)
  }, [siteId, departmentOptions, emptyValue, requireParent])

  const filteredUnitOptions = useMemo(() => {
    if (departmentId !== emptyValue) {
      return unitOptions.filter(
        (unit) => unit.departmentId == null || String(unit.departmentId) === departmentId,
      )
    }
    if (!requireParent && siteId !== emptyValue) {
      return unitOptions.filter((unit) => unit.siteId == null || String(unit.siteId) === siteId)
    }
    return requireParent ? [] : unitOptions
  }, [siteId, departmentId, unitOptions, emptyValue, requireParent])

  const handleSiteChange = (val: string) => {
    setSiteId(val)
    setDepartmentId(emptyValue)
    setUnitId?.(emptyValue)
  }

  const handleDepartmentChange = (val: string) => {
    setDepartmentId(val)
    setUnitId?.(emptyValue)
  }

  return {
    filteredDepartmentOptions,
    filteredUnitOptions,
    handleSiteChange,
    handleDepartmentChange,
  }
}
